import { useState, useCallback, useRef, useEffect } from 'react';
import AutocompleteInput from '@/components/base/AutocompleteInput';
import { filterItems, mapApiToItem } from '@/api/item.api';
import { SerialEntryModal } from '@/pages/purchase/grn/components/SerialEntryModal';
import { useUnits, switchLineUnit } from '@/hooks/useUnits';
import LineUnitSelect from '@/components/common/LineUnitSelect';
import {
  type SmartItemOption,
  type SmartRowData,
} from '../smartRow.types';

export type { SmartItemOption, SmartRowData };

interface SmartItemRowProps {
  row: SmartRowData;
  rowIdx: number;
  onUpdate: (id: string, patch: Partial<SmartRowData>) => void;
  onRemove: (id: string) => void;
  /** Called when user presses Enter on Rate field (last nav step) */
  onEnterOnRate: () => void;
  isLast: boolean;
  /** Warehouse ID — items are scoped to this warehouse when provided */
  warehouseId?: string;
  /** Optional extra column content (e.g. PO Ref badge) */
  extraColumn?: React.ReactNode;
  /** Expose the smart input ref for external focus */
  smartInputRef?: (el: HTMLInputElement | null) => void;
  showBatchFields?: boolean;
}

const isBarcode = (val: string) => /^\d{8,13}$/.test(val.trim());

export default function SmartItemRow({
  row,
  rowIdx,
  onUpdate,
  onRemove,
  onEnterOnRate,
  isLast,
  warehouseId,
  extraColumn,
  smartInputRef,
  showBatchFields = false,
}: SmartItemRowProps) {
  const [inputVal, setInputVal] = useState(row.itemName || '');
  const [barcodeMsg, setBarcodeMsg] = useState<{ type: 'ok' | 'warn'; text: string } | null>(null);
  const [itemOptions, setItemOptions] = useState<SmartItemOption[]>([]);
  const [qtyDisplay, setQtyDisplay] = useState(String(row.qty));
  const units = useUnits();
  const lineUnit = units.byId(row.enteredUnitId) ?? units.byCode(row.unit);
  // Whole numbers only in units that count whole (Pcs); others take up to 6 decimals
  const wholeOnly = Boolean(lineUnit && !lineUnit.allowDecimal);
  const toStock = units.toStock(row.enteredUnitId, row.unit);
  const [showSerialModal, setShowSerialModal] = useState(false);
  const qtyRef = useRef<HTMLInputElement>(null);
  const rateRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (row.itemName) setInputVal(row.itemName);
  }, [row.itemName]);

  useEffect(() => {
    let active = true;

    const loadItems = async () => {
      try {
        // Use filterItems with warehouseId when available so only items
        // belonging to the selected warehouse appear in the search dropdown.
        const response = await filterItems(
          warehouseId && warehouseId !== 'ALL'
            ? { warehouseId }
            : {},
        );
        const mapped = (response.data || []).map((item) => {
          const normalized = mapApiToItem(item);
          return {
            id: normalized.id,
            name: normalized.name,
            code: normalized.code,
            barcode: normalized.barcode,
            unitName: normalized.unitName,
            hsnCode: normalized.hsnCode,
            purchaseRate: normalized.purchaseRate,
            currentStock: normalized.stock ?? 0,
            isSerialTracked: normalized.isSerialTracked,
            serialPrefix: normalized.serialPrefix,
            defaultWarrantyMonths: normalized.defaultWarrantyMonths,
            isBatchTracked: normalized.isBatchTracked,
            requiresIncomingQC: Boolean(normalized.requiresIncomingQC || (item as any).requires_incoming_qc),
          };
        });

        if (active) {
          setItemOptions(mapped);
        }
      } catch {
        if (active) {
          setItemOptions([]);
        }
      }
    };

    void loadItems();

    return () => {
      active = false;
    };
  }, [warehouseId]);

  useEffect(() => {
    setQtyDisplay(String(row.qty));
  }, [row.qty]);

  const applyItem = useCallback(
    (item: SmartItemOption) => {
      const total = row.qty * item.purchaseRate;
      onUpdate(row.id, {
        itemId: item.id,
        itemName: item.name,
        hsnCode: item.hsnCode,
        barcode: item.barcode,
        unit: item.unitName,
        enteredUnitId: null,
        rate: item.purchaseRate,
        total,
        isSerialTracked: item.isSerialTracked,
        serialPrefix: item.serialPrefix,
        defaultWarrantyMonths: item.defaultWarrantyMonths,
        isBatchTracked: item.isBatchTracked,
        requiresIncomingQC: item.requiresIncomingQC ?? false,
        serials: [],
      });
      setInputVal(item.name);
      setBarcodeMsg(null);
      if (item.isSerialTracked) {
        setTimeout(() => setShowSerialModal(true), 200);
      } else {
        setTimeout(() => qtyRef.current?.focus(), 60);
      }
    },
    [row.id, row.qty, onUpdate]
  );

  // Called by AutocompleteInput's externalKeyDown when dropdown is closed
  const handleSmartKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return;
      const val = inputVal.trim();
      if (!val) return;
      if (isBarcode(val)) {
        e.preventDefault();
        const found = itemOptions.find((i) => i.barcode === val);
        if (found) {
          applyItem(found);
          setBarcodeMsg({ type: 'ok', text: `Found: ${found.name}` });
          setTimeout(() => setBarcodeMsg(null), 2500);
        } else {
          setBarcodeMsg({ type: 'warn', text: 'Item not found — add to masters first' });
          setTimeout(() => setBarcodeMsg(null), 3500);
          setInputVal('');
        }
      }
    },
    [inputVal, applyItem, itemOptions]
  );

  const fetchItems = useCallback(async (q: string) => {
    const lower = q.toLowerCase().trim();
    if (!lower) return itemOptions;
    return itemOptions.filter(
      (i) =>
        i.name.toLowerCase().includes(lower) ||
        i.code.toLowerCase().includes(lower) ||
        i.barcode.toLowerCase().includes(lower)
    );
  }, [itemOptions]);

  const handleQtyChange = (val: number) => {
    const qty = val > 0 ? val : 1;
    onUpdate(row.id, { qty, total: qty * row.rate });
  };

  // Typed qty → number; empty, 0 or invalid falls back to 1
  const parseQty = (text: string) => {
    const parsed = wholeOnly ? parseInt(text, 10) : parseFloat(text);
    return !text || isNaN(parsed) || parsed <= 0 ? 1 : parsed;
  };

  const handleUnitChange = (unitId: string | null) => {
    const next = switchLineUnit(units, row.unit, row.enteredUnitId, unitId, row.qty, row.rate);
    const rate = next.rate ?? row.rate;
    setQtyDisplay(String(next.qty));
    onUpdate(row.id, { enteredUnitId: unitId, qty: next.qty, rate, total: next.qty * rate });
  };

  const handleRateChange = (val: number) => {
    const rate = val < 0 ? 0 : val;
    onUpdate(row.id, { rate, total: row.qty * rate });
  };

  const clearItem = () => {
    onUpdate(row.id, {
      itemId: '',
      itemName: '',
      hsnCode: '',
      barcode: '',
      unit: '',
      enteredUnitId: null,
      rate: 0,
      total: 0,
      requiresIncomingQC: false,
    });
    setInputVal('');
    setBarcodeMsg(null);
  };

  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50/50 group">
      <td className="px-3 py-2 text-xs text-slate-400 text-center w-8">{rowIdx + 1}</td>

      {/* Smart Item Input */}
      <td className="px-2 py-1.5" style={{ minWidth: 380 }}>
        {row.itemId ? (
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-[#1e293b] font-medium truncate flex-1">{row.itemName}</span>
              <button
                type="button"
                onClick={clearItem}
                title="Change or re-search item"
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded text-indigo-600 bg-indigo-50 hover:bg-indigo-100 cursor-pointer transition-colors"
              >
                <i className="ri-search-line text-xs" />
                Change
              </button>
              <button
                type="button"
                onClick={clearItem}
                title="Clear item"
                className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-red-500 cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-xs" />
              </button>
            </div>
            {row.isSerialTracked && (
              <div className="mt-1 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowSerialModal(true)}
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border cursor-pointer transition-colors ${
                    (row.serials?.length || 0) === Math.round(row.qty * toStock)
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                      : 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 animate-pulse'
                  }`}
                >
                  <i className="ri-barcode-box-line" />
                  {(row.serials?.length || 0) === Math.round(row.qty * toStock)
                    ? `${row.serials?.length} Serials Recorded ✓`
                    : `Enter Serials (${row.serials?.length || 0}/${Math.round(row.qty * toStock)} entered)`}
                </button>
                <span className="text-[11px] text-slate-400">
                  Prefix: <span className="font-mono font-medium text-slate-600">{row.serialPrefix || 'SN'}</span> · Warranty: <span className="font-medium text-slate-600">{row.defaultWarrantyMonths || 12} mo</span>
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full">
            <AutocompleteInput<SmartItemOption>
              value={inputVal}
              onChange={setInputVal}
              onSelect={applyItem}
              fetchOptions={fetchItems}
              placeholder="Search item name, code or scan barcode..."
              renderOption={(item) => (
                <div className="py-0.5">
                  <div className="flex items-center gap-1.5 font-medium text-xs text-slate-800">
                    <span>{item.name}</span>
                    {item.requiresIncomingQC ? (
                      <span className="text-[10px] px-1.5 py-0.2 bg-amber-50 text-amber-700 border border-amber-200 rounded font-semibold">
                        QC Required
                      </span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">
                        No QC
                      </span>
                    )}
                    {item.isSerialTracked && (
                      <span className="text-[10px] px-1.5 py-0.2 bg-blue-50 text-blue-700 border border-blue-200 rounded font-semibold">
                        Serial
                      </span>
                    )}
                    {item.isBatchTracked && (
                      <span className="text-[10px] px-1.5 py-0.2 bg-purple-50 text-purple-700 border border-purple-200 rounded font-semibold">
                        Batch
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{item.code} · Stock: {item.currentStock} {item.unitName}</div>
                </div>
              )}
              getOptionKey={(i) => i.id}
              getOptionLabel={(i) => i.name}
              minChars={0}
              className="w-full"
              inputClassName="w-full h-8 px-3 pr-7 text-sm"
              onKeyDown={handleSmartKeyDown}
              inputRef={smartInputRef}
            />
            {barcodeMsg && (
              <p className={`text-[10px] font-medium mt-0.5 ${barcodeMsg.type === 'ok' ? 'text-emerald-600' : 'text-amber-600'
                }`}>
                <i className={`mr-0.5 ${barcodeMsg.type === 'ok' ? 'ri-checkbox-circle-line' : 'ri-alert-line'}`} />
                {barcodeMsg.text}
              </p>
            )}
          </div>
        )}
      </td>

      {/* QC Status Column */}
      <td className="px-2 py-1.5 text-center w-28 whitespace-nowrap">
        {row.itemId ? (
          row.requiresIncomingQC ? (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 shadow-xs"
              title="Incoming QC is required. Stock will not be increased until inspection is passed."
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              True (QC Required)
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
              title="QC not required. Stock updates immediately on GRN save."
            >
              <i className="ri-check-line text-emerald-600 text-xs" />
              False (Not Required)
            </span>
          )
        ) : (
          <span className="text-slate-300 text-xs font-mono">—</span>
        )}
      </td>

      {/* Batch Tracking Fields */}
      {showBatchFields && (
        <td className="px-2 py-1.5 w-44">
          {row.itemId && !row.isBatchTracked ? (
            <span className="text-slate-400 text-xs italic">Not batch tracked</span>
          ) : row.isAutoBatch !== false ? (
            <div className="flex items-center gap-1.5">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-mono font-medium">
                <i className="ri-magic-line text-indigo-500 text-[11px]" />
                Auto (B-GRN-...)
              </span>
              <button
                type="button"
                title="Enter custom / vendor batch"
                onClick={() => onUpdate(row.id, { isAutoBatch: false, batchNumber: '' })}
                className="p-1 text-slate-400 hover:text-indigo-600 rounded hover:bg-slate-100 cursor-pointer"
              >
                <i className="ri-edit-line text-xs" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <input
                type="text"
                placeholder="Vendor batch..."
                value={row.batchNumber || ''}
                onChange={(e) => onUpdate(row.id, { batchNumber: e.target.value })}
                className="w-full h-8 px-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 font-mono"
              />
              <button
                type="button"
                title="Reset to Auto batch"
                onClick={() => onUpdate(row.id, { isAutoBatch: true, batchNumber: '' })}
                className="p-1 text-slate-400 hover:text-indigo-600 rounded hover:bg-slate-100 cursor-pointer"
              >
                <i className="ri-refresh-line text-xs" />
              </button>
            </div>
          )}
        </td>
      )}

      {showBatchFields && (
        <td className="px-2 py-1.5 w-32">
          {row.itemId && !row.isBatchTracked ? (
            <span className="text-slate-300 text-xs italic">—</span>
          ) : (
            <input
              type="date"
              value={row.mfgDate || ''}
              onChange={(e) => onUpdate(row.id, { mfgDate: e.target.value })}
              className="w-full h-8 px-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 text-slate-700"
            />
          )}
        </td>
      )}

      {showBatchFields && (
        <td className="px-2 py-1.5 w-32">
          {row.itemId && !row.isBatchTracked ? (
            <span className="text-slate-300 text-xs italic">—</span>
          ) : (
            <input
              type="date"
              value={row.expiryDate || ''}
              onChange={(e) => onUpdate(row.id, { expiryDate: e.target.value })}
              placeholder="Optional"
              className="w-full h-8 px-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 text-slate-600"
            />
          )}
        </td>
      )}

      {/* Qty */}
      <td className="px-2 py-1.5 w-24">
        <input
          ref={qtyRef}
          type="text"
          inputMode={wholeOnly ? 'numeric' : 'decimal'}
          value={qtyDisplay}
          onChange={(e) => {
            const raw = e.target.value;
            if (!(wholeOnly ? /^\d*$/ : /^\d*\.?\d{0,6}$/).test(raw)) return;
            setQtyDisplay(raw); // allow empty string / "2." while typing
            const parsed = parseFloat(raw);
            if (parsed > 0) handleQtyChange(parsed);
          }}
          onBlur={() => {
            // On blur, if empty or 0, snap back to 1
            const safe = parseQty(qtyDisplay);
            setQtyDisplay(String(safe));
            handleQtyChange(safe);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault();
            } else if (e.key === 'Enter') {
              e.preventDefault();
              // Commit before moving focus
              const safe = parseQty(qtyDisplay);
              setQtyDisplay(String(safe));
              handleQtyChange(safe);
              rateRef.current?.focus();
            }
          }}
          placeholder="1"
          className="w-full h-8 px-2 text-sm text-right border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
        />
      </td>

      {/* Unit: any unit of the item's stock-unit category */}
      <td className="px-2 py-1.5 w-20">
        {row.unit ? (
          <LineUnitSelect catalog={units} stockUnit={row.unit} value={row.enteredUnitId} onChange={handleUnitChange} />
        ) : (
          <span className="text-sm text-slate-500">—</span>
        )}
      </td>

      {/* Rate (editable, optional) */}
      <td className="px-2 py-1.5 w-28">
        <input
          ref={rateRef}
          type="text"
          inputMode="decimal"
          pattern="[0-9]*(\.[0-9]*)?"
          value={row.rate === 0 ? '' : row.rate}
          onChange={(e) => {
            const val = e.target.value.replace(/[^\d.]/g, '');
            if (val === '') {
              onUpdate(row.id, { rate: 0, total: 0 });
            } else {
              handleRateChange(parseFloat(val) || 0);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault();
            } else if (e.key === 'Enter') {
              e.preventDefault();
              if (isLast) onEnterOnRate();
            }
          }}
          placeholder="0.00"
          className="w-full h-8 px-2 text-sm text-right border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
        />
      </td>

      {/* Extra column (PO Ref for supplier mode) */}
      {extraColumn !== undefined && (
        <td className="px-2 py-1.5 w-36">{extraColumn}</td>
      )}

      {/* Total (read-only, blank if rate is 0 or qty is 0) */}
      <td className="px-2 py-1.5 w-28 text-right">
        <span className="text-sm font-medium text-[#1e293b]">
          {row.itemId && row.rate > 0 && row.qty > 0
            ? `₹${(row.qty * row.rate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '—'}
        </span>
      </td>

      {/* Delete */}
      <td className="px-2 py-1.5 w-10">
        <button
          type="button"
          onClick={() => onRemove(row.id)}
          className="w-6 h-6 flex items-center justify-center text-slate-300 hover:text-red-500 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <i className="ri-delete-bin-line text-sm" />
        </button>
      </td>
    </tr>
    {showSerialModal && (
      <SerialEntryModal
        isOpen={showSerialModal}
        onClose={() => setShowSerialModal(false)}
        itemName={row.itemName}
        itemCode={row.barcode || undefined}
        serialPrefix={row.serialPrefix || undefined}
        requiredQty={Math.round(row.qty * toStock)}
        initialSerials={row.serials || []}
        onSave={(serials) => {
          onUpdate(row.id, { serials });
          setShowSerialModal(false);
        }}
      />
    )}
  </>
  );
}
